import {EventEmitter, Injectable, OnDestroy} from "@angular/core";
import {ChannelConfig, ChannelDefinition} from "../../model/channels.model";
import {HttpClient} from "@angular/common/http";
import {ViewManagerService} from "../../services/view-manager.service";
import {environment} from "../../../environments/environment";
import {Level, Message} from "../../model/message.component";
import {ObjectMapper} from "json-object-mapper";
import {DataEntry, SingleLookupRequest, SingleLookupRequestFieldData} from "../../model/fields.model";
import {ChannelsService} from "../channels/channels-service.service";
import {ResultCase, RiskFactor, ScreeningAlert} from "../../model/lookup-results.model";
import {BehaviorSubject} from "rxjs";
import {map} from "rxjs/operators";
import {TranslateService} from "@ngx-translate/core";

declare var bootstrap: any;

@Injectable({
  providedIn: "root"
})
export class LookupService implements OnDestroy {

  definition: ChannelDefinition;

  entry: BehaviorSubject<DataEntry>;

  channels: ChannelConfig[] = [];

  fields: SingleLookupRequestFieldData[] = [];

  checksResult: ScreeningAlert[] = [];

  rulesResult: RiskFactor[] = [];

  resultCase: ResultCase;

  constructor(
    private http: HttpClient,
    private viewManager: ViewManagerService,
    private channelService: ChannelsService,
    private translate: TranslateService,
  ) {
    console.log("- SERVICE 'LOOKUP' CONSTRUCTOR -");
    this.initState();
  }

  ngOnDestroy() {
    console.log("- SERVICE 'LOOKUP' STOPPING -");
  }

  initState() {
    console.log("- SERVICE 'LOOKUP' INITIALIZING -");
    this.definition = new ChannelDefinition();
    this.channels = [];
    this.fields = [];
    this.entry = new BehaviorSubject<DataEntry>(new DataEntry());
    this.checksResult = [];
    this.rulesResult = [];
  }

  load(vertical: string, frontend: string) {
    this.channelService.functionality(vertical, frontend)
      .pipe()
      .subscribe({
        next: (result) => {
          this.channels = result;
          console.log(this.channels);
          this.select(this.channels[0]);
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")))
        }
      })
  }

  select(cc: ChannelConfig) {
    this.channelService.view(cc.owner!, cc.vertical!, cc.code!)
      .pipe()
      .subscribe({
        next: (result) => {
          if (result && result.input) {
            this.definition = result;
            let fields = result.input?.fields?.map((config) => {
              let json = {
                "name": config.name,
                "config": config,
                "params": {
                  "threshold": 0.0
                }
              }
              return Object.assign(new SingleLookupRequestFieldData(), json);
            });

            if (fields) {
              this.fields = fields;
            }
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")))
        }
      });
  }

  private params() {
    let config = this.definition.config!;
    let json = {
      "dataOwner": config.owner,
      "dataVertical": config.vertical,
      "dataCode": config.code,
      "fields": this.fields.filter((f) => f.value).map(f => {
        let updated = structuredClone(f)
        if (f.params.threshold != 0) {
          updated.params.threshold = f.params.threshold / 100;
        }
        return updated;
      })
    }
    return JSON.parse(String(ObjectMapper.serialize(Object.assign(new SingleLookupRequest(), json))));
  }

  private lookup() {
    return this.http.post(environment.apiPrefix + "/" + this.definition.config!.process + "/lookup", this.params());
  }

  upload(fileUpload: File): void {
    if (fileUpload) {
      const formData: FormData = new FormData();
      formData.append("fileUpload", fileUpload);
      // formData.append("model", );
      formData.append("owner", this.definition.config!.owner!);
      formData.append("vertical", this.definition.config!.vertical!);
      formData.append("code", this.definition.config!.code!);
      formData.append("model", this.definition.input!.settings!["ocr"]!);

      this.http.post(`${environment.servicesOcrPrefix}/image`, formData, {
        "reportProgress": true,
        "responseType": "json"
      }).subscribe((res: any) => {
        const data = res.data;
        this.fields = this.fields.map(field => ({
          ...field,
          value: res.data[field.name!] ?? field.value
        }));
      });
    }
  }

  reset(vertical: string, frontend: string) {
    this.initState();
    this.load(vertical, frontend);
  }

  refine() {
    this.checksResult = [];

    this.rulesResult = [];
    this.entry = new BehaviorSubject<DataEntry>(new DataEntry());
  }

  screening() {
    this.lookup()
      .pipe(map(result => ObjectMapper.deserializeArray(ScreeningAlert, result)))
      .subscribe({
        next: (result) => {
          if (result && result.length > 0) {
            this.checksResult = result;
          } else {
            this.viewManager.displayMessage(new Message("no results found", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")));
        }
      });
  }

  rules() {
    this.lookup()
      .pipe(map(result => ObjectMapper.deserializeArray(RiskFactor, result)))
      .subscribe({
        next: (result) => {
          if (result && result.length > 0) {
            this.rulesResult = result;
            console.log(this.rulesResult);
          } else {
            this.viewManager.displayMessage(new Message("no results found", new Level("warning")));
          }
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")));
        }
      });
  }

  report() {
    this.http.post(environment.apiPrefix + "/" + this.definition.config!.process + "/report", this.params())
      .pipe()
      .subscribe({
        next: (result) => {
          this.resultCase = ObjectMapper.deserialize(ResultCase, result);
          console.log(this.resultCase);
          if (this.resultCase.external) {
            this.viewManager.displayMessage(new Message("case is available at: " + this.resultCase.external, new Level("info")), 30000);
          } else {
            this.viewManager.displayMessage(new Message("case manager is unavailable at the moment", new Level("warning")));
          }
          // http://localhost:4200/#/admin/results/case/view/PISRM-17472 <- OK
          // http://localhost:4200/#/admin/results/case/view/UWVVT-68432 <- NOK

          this.viewManager.navigate(["/admin/results/case/view", this.resultCase.internal]);
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")));
        }
      });
  }

  view(owner: string, vertical: string, code: string, reference: string) {
    let path = "/entry/" + owner + "/" + vertical + "/" + code + "/" + reference;
    return this.http.get<DataEntry>(environment.apiPrefix + path)
      .subscribe({
        next: (result) => {
          this.entry.next(result);
        },
        error: error => {
          this.viewManager.displayMessage(new Message(error.error, new Level("error")));
        }
      });
  }

  viewEntry(titleEmitter: EventEmitter<string>, alert: ScreeningAlert) {
    this.view(alert.list!.owner!, alert.list!.vertical!, alert.list!.code!, alert.reference!);
    this.entry.subscribe((value) => {
      if (value) {
        this.translate.get("pages.lookup.entry.dialogue.title", value).subscribe((resource: string) => {
          titleEmitter.emit(resource);
        });
      } else {
        titleEmitter.emit("pages.lookup.entry.dialogue.title");
      }
    });

    const modalEl = document.getElementById("defaultModal");
    if (modalEl) {
      const modal = new bootstrap.Modal(modalEl);
      modal.show();

      modalEl.addEventListener("hidden.bs.modal", () => {
        this.entry.next(new DataEntry())
        titleEmitter.emit("");
      });
    }
  }

  riskClass(risk: string) {
    if (risk == "high") {
      return "danger";
    } else if (risk == "medium") {
      return "warning";
    } else if (risk == "low") {
      return "success";
    } else if (risk == "none") {
      return "secondary";
    }
    return "danger";
  }
}
